import { GenreOut, ProductOut, SubGenreOut } from '../store/api';
import { GenreMetaData, ProductMetaData, SubGenreMetaData } from '../../models/Product';
import { LoanFormat, mapParsedLoanFormat } from '../../models/LoanFormat';
import { isNotBlank, isOneOf, isPresent, validateAll, validateAndLogOnErrors, validateField } from './validation';
import {
    PRODUCT_CLASS_CAMPAIGN,
    PRODUCT_CLASS_LANGUAGE_LEARNING,
    PRODUCT_CLASS_LOCAL,
    PRODUCT_CLASS_LOTE,
    PRODUCT_CLASS_STANDARD
} from '../store/constants';
import { AgeCategory } from '../../models/AgeCategory';
import { mapValidItems } from './mapping';

const validateId = validateAll(isPresent, isNotBlank);

const validateAgeCategory = validateAll(
    isOneOf(AgeCategory.CHILDREN, AgeCategory.YOUNG_ADULT, AgeCategory.ADULT, AgeCategory.ALL_AGES)
);

const validateProductClass = validateAll(
    isPresent,
    isOneOf(
        PRODUCT_CLASS_STANDARD,
        PRODUCT_CLASS_CAMPAIGN,
        PRODUCT_CLASS_LOTE,
        PRODUCT_CLASS_LANGUAGE_LEARNING,
        PRODUCT_CLASS_LOCAL
    )
);

const validateLoanFormat = validateAll(
    isPresent,
    isOneOf(
        LoanFormat.eBooks,
        LoanFormat.eAudiobooks,
        LoanFormat.eMagazines,
        LoanFormat.ePress,
        'EBOOKS',
        'EAUDIOBOOKS',
        'EMAGAZINES',
        'EPRESS'
    )
);

export const productOutValidation = validateAll<ProductOut>(
    validateField('id', validateId),
    validateField('productClass', validateProductClass),
    validateField('loanFormat', validateLoanFormat),
    validateField('ageCategory', validateAgeCategory)
);

const validateGenreName = validateAll(isPresent, isNotBlank);
const genreValidation = validateAll<GenreOut>(
    validateField('id', validateId),
    validateField('name', validateGenreName)
);

const isGenreValid = (genre: GenreOut): boolean => {
    return validateAndLogOnErrors(genreValidation, genre);
};

const mapSubGenre = (subGenres?: ReadonlyArray<SubGenreOut>): ReadonlyArray<SubGenreMetaData> => {
    if (!subGenres) {
        return [];
    }

    return mapValidItems(subGenres, isGenreValid, genre => ({
        id: genre.id!,
        name: genre.name!
    }));
};

export const mapGenres = (genres?: ReadonlyArray<GenreOut>): ReadonlyArray<GenreMetaData> => {
    if (!genres) {
        return [];
    }

    return mapValidItems(genres, isGenreValid, genre => ({
        id: genre.id!,
        name: genre.name!,
        subGenres: mapSubGenre(genre.subGenres)
    }));
};

export const mapProductOutToProductMetaDataArray = (
    products?: ReadonlyArray<ProductOut>
): ReadonlyArray<ProductMetaData> => {
    if (!products) {
        return [];
    }

    return products.map(mapProductOutToProductMetaData).filter(p => p !== EMPTY_PRODUCT);
};

export const EMPTY_PRODUCT: ProductMetaData = {
    id: '',
    loanFormat: LoanFormat.eBooks,
    productClass: 'STANDARD'
};

export const mapProductOutToProductMetaData = (product?: ProductOut): ProductMetaData => {
    if (!product || !validateAndLogOnErrors(productOutValidation, product)) {
        return EMPTY_PRODUCT;
    }

    return {
        ...(product as ProductMetaData), // TODO complete validations and mappings
        loanFormat: mapParsedLoanFormat(product.loanFormat),
        genres: mapGenres(product.genres)
    };
};
